import { ChangeEvent, FC } from 'react';
import { Button, CircularProgress, Grid, InputLabel, Stack } from '@mui/material';
import { Avatar } from '../../components/Avatar';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { useTheme } from '@mui/material/styles';
import { useImageUpload } from '../../images/useImageUpload';
import { useSnackbar } from '../../store/snackbar';
import { useModalContext } from '../../modal/ModalContext';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';

const getPersonaQuery = graphql(`
	query editPersonaAvatar_getPersona($id: UUID!) {
		getPersona(by: { id: $id }) {
			avatar {
				url(width: 64, height: 64)
			}
		}
	}
`);

const updatePersonaMutation = graphql(`
	mutation editPersonaAvatar_updatePersona($personaId: UUID!, $imageId: UUID!) {
		updatePersona(by: { id: $personaId }, data: { avatar: { connect: { id: $imageId } } }) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<{
	personaId: string;
}>;

const EditPersonaAvatar: FC<Props> = ({ personaId }) => {
	const theme = useTheme();
	const intl = useIntl();
	const { setModal } = useModalContext();
	const { openSnackbar } = useSnackbar();

	const { data: getPersonaResult } = useApiQuery(getPersonaQuery, {
		variables: { id: personaId },
	});

	const { mutate: updatePersona, isPending: isUpdatePersonaPending } = useApiMutation(updatePersonaMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updatePersona.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'persona.avatar.update.error' }),
				},
				errorMessage: result.updatePersona.errorMessage,
				openSnackbar,
			});
		},
	});

	const { uploadImage, isLoading } = useImageUpload({
		onSuccess: (imageId) => {
			updatePersona({
				personaId,
				imageId,
			});
		},
		onError: () => {
			openSnackbar({
				open: true,
				message: intl.formatMessage({ id: 'common.error.default' }),
				variant: 'alert',
				alert: {
					color: 'error',
				},
				close: true,
			});
		},
	});

	const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file != null) {
			uploadImage(file);
			event.target.value = ''; // resets files of the input
		}
	};

	const handleRemove = () => {
		setModal({
			type: 'removePersonaAvatar',
			payload: {
				personaId,
			},
		});
	};

	return (
		<Grid item xs={12}>
			<Stack spacing={1}>
				<InputLabel>
					<FormattedMessage id={'drawer.edit-persona-detail.label.photo'} />
				</InputLabel>
				{isLoading || isUpdatePersonaPending ? (
					<Stack
						sx={{ backgroundColor: theme.palette.grey[400], width: 64, height: 64, borderRadius: 32 }}
						alignItems={'center'}
						justifyContent={'center'}
					>
						<CircularProgress />
					</Stack>
				) : (
					<Stack direction={'row'} gap={1}>
						<Avatar
							src={getPersonaResult?.getPersona?.avatar?.url ?? ''}
							alt={'Your photo'}
							sx={{ width: 64, height: 64, backgroundColor: theme.palette.grey[400] }}
						/>
						<Stack direction={'row'} gap={0} alignItems={'center'}>
							<Button
								component={'label'}
								sx={{
									height: 24,
									color: (theme) => (getNightOrDayPalette(theme) === 'night' ? theme.palette.primary.main : theme.palette.day.A100),
								}}
							>
								<FormattedMessage id={'drawer.edit-persona-detail.avatar.upload'} />
								<input type={'file'} accept={'image/*'} hidden onChange={handleUpload} />
							</Button>
							{getPersonaResult?.getPersona?.avatar?.url != null && (
								<Button color={'secondary'} sx={{ height: 24 }} onClick={handleRemove}>
									<FormattedMessage id={'drawer.edit-persona-detail.avatar.delete'} />
								</Button>
							)}
						</Stack>
					</Stack>
				)}
			</Stack>
		</Grid>
	);
};

export { EditPersonaAvatar };
